import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  Building2, MapPin, Mail, Phone, Receipt, Landmark, CreditCard,
  Save, Loader2, CheckCircle2, Key, Eye, EyeOff, ExternalLink,
  Trash2, Image, PenTool, Globe, FileText, Shield, Clock,
  AlertTriangle, Upload, X, Users, Plus, Send, XCircle, ListPlus,
  Briefcase, Lock, Info
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { FeatureGate } from "../components/FeatureGate";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "company", label: "Company Profile", icon: Building2, caOnly: false },
  { id: "invoice", label: "Invoice & Tax", icon: FileText, caOnly: false },
  { id: "bank", label: "Bank & UPI", icon: Landmark, caOnly: false },
  { id: "branding", label: "Logo & Signature", icon: Image, caOnly: false },
  { id: "custom", label: "Additional Fields", icon: ListPlus, caOnly: false },
  { id: "ca", label: "CA/Professional Management", icon: Users, caOnly: false },
  { id: "security", label: "Security", icon: Lock, caOnly: false },
  { id: "api", label: "API Keys", icon: Key, caOnly: false },
];

const CA_TABS = [
  { id: "profile", label: "Professional Profile", icon: Shield },
  { id: "security", label: "Security", icon: Lock },
  { id: "api", label: "API Keys", icon: Key },
];

const PAYMENT_TERMS_OPTIONS = [
  { value: "Due on Receipt", label: "Due on Receipt", days: 0 },
  { value: "Net 7", label: "Due in 7 Days", days: 7 },
  { value: "Net 15", label: "Due in 15 Days", days: 15 },
  { value: "Net 30", label: "Due in 30 Days", days: 30 },
  { value: "Net 45", label: "Due in 45 Days", days: 45 },
  { value: "Net 60", label: "Due in 60 Days", days: 60 },
  { value: "Net 90", label: "Due in 90 Days", days: 90 },
  { value: "Custom", label: "Custom Due Date", days: -1 },
];

export default function SettingsPage() {
  const { user, isAuthenticated, defaultCompanyId, isCA } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tabFromUrl = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(isCA ? "profile" : (tabFromUrl || "company"));
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [gstinValidating, setGstinValidating] = useState(false);
  const [gstinValid, setGstinValid] = useState(null);

  // API Key state
  const [apiKey, setApiKey] = useState("");
  const [maskedKey, setMaskedKey] = useState(null);
  const [hasKey, setHasKey] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [savingKey, setSavingKey] = useState(false);
  const [deletingKey, setDeletingKey] = useState(false);

  // Password / account-linking state
  const [hasPassword, setHasPassword] = useState(null);  // null=loading, true/false=resolved
  const [authProvider, setAuthProvider] = useState("email");
  const [pwdCurrent, setPwdCurrent] = useState("");
  const [pwdNew, setPwdNew] = useState("");
  const [pwdConfirm, setPwdConfirm] = useState("");
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdShow, setPwdShow] = useState(false);

  // Upload state
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingSig, setUploadingSig] = useState(false);
  const logoRef = useRef(null);
  const sigRef = useRef(null);

  // CA Management state
  const [caInviteEmail, setCaInviteEmail] = useState("");
  const [invitingCA, setInvitingCA] = useState(false);
  const [caMappings, setCaMappings] = useState([]);

  // Professional Profile state (for CA users)
  const [proProfile, setProProfile] = useState({
    name: "",
    firm_name: "",
    specialization: "",
    location: "",
    bio: "",
    is_public: true,
  });
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      // Always fetch /auth/me-derived state (hasPassword, authProvider) for every user —
      // it drives the Security tab's password form. Was previously CA-only, which
      // left Google-signed-in clients with hasPassword=true (default) and forced them
      // to enter a non-existent current password.
      fetchProProfile();
    }
    if (isAuthenticated && isCA) {
      fetchApiKeyStatus();
      setLoading(false);
    } else if (isAuthenticated && defaultCompanyId) {
      fetchCompany();
      fetchApiKeyStatus();
      fetchCAMappings();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, defaultCompanyId, isCA]);

  const fetchCompany = async () => {
    try {
      const res = await axios.get(`${API}/companies/${defaultCompanyId}`, { withCredentials: true });
      setCompany(res.data);
      setForm(res.data);
      if (res.data.gst_number?.length === 15) setGstinValid(true);
    } catch {
      toast.error("Failed to load company details");
    } finally {
      setLoading(false);
    }
  };

  const fetchProProfile = async () => {
    try {
      const meRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
      const u = meRes.data;
      setHasPassword(!!u.has_password);
      setAuthProvider(u.auth_provider || "email");
      const profile = u.professional_profile || {};
      setProProfile({
        name: u.name || "",
        firm_name: profile.firm_name || "",
        specialization: profile.specialization || "",
        location: profile.location || "",
        bio: profile.bio || "",
        is_public: profile.is_public !== false,
      });
    } catch { /* ignore */ }
  };

  const handleSavePassword = async () => {
    if (pwdNew.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pwdNew !== pwdConfirm) { toast.error("Passwords don't match"); return; }
    if (hasPassword && !pwdCurrent) { toast.error("Enter your current password"); return; }
    setPwdSaving(true);
    try {
      await axios.post(`${API}/auth/set-password`, {
        new_password: pwdNew,
        current_password: pwdCurrent || undefined,
      }, { withCredentials: true });
      toast.success(hasPassword ? "Password changed successfully" : "Password set — you can now login with email + password");
      setHasPassword(true);
      setPwdCurrent(""); setPwdNew(""); setPwdConfirm("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update password");
    } finally {
      setPwdSaving(false);
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await axios.put(`${API}/ca/profile`, proProfile, { withCredentials: true });
      toast.success("Professional profile saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const fetchApiKeyStatus = async () => {
    try {
      const res = await axios.get(`${API}/settings/api-key`, { withCredentials: true });
      setHasKey(res.data.has_key);
      setMaskedKey(res.data.masked_key);
    } catch {}
  };

  const update = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const fetchCAMappings = async () => {
    try {
      const res = await axios.get(`${API}/ca/mappings`, { withCredentials: true });
      setCaMappings(res.data.mappings || []);
    } catch { /* ignore */ }
  };

  const handleInviteCA = async () => {
    if (!caInviteEmail.trim() || !caInviteEmail.includes("@")) { toast.error("Enter a valid CA/Professional email"); return; }
    setInvitingCA(true);
    try {
      await axios.post(`${API}/ca/invite`, {
        email: caInviteEmail.trim(),
        role_of_invitee: "ca",
        permissions: ["view", "export"],
      }, { withCredentials: true });
      toast.success("Invitation sent to CA!");
      setCaInviteEmail("");
      fetchCAMappings();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to invite");
    } finally {
      setInvitingCA(false);
    }
  };

  const handleRevokeCA = async (mappingId) => {
    if (!window.confirm("Revoke this CA/Professional's access?")) return;
    try {
      await axios.post(`${API}/ca/invite/${mappingId}/revoke`, {}, { withCredentials: true });
      toast.success("Access revoked");
      fetchCAMappings();
    } catch {
      toast.error("Failed to revoke");
    }
  };

  const handleGstinChange = async (gstin) => {
    update("gst_number", gstin);
    setGstinValid(null);
    if (gstin.length === 15) {
      setGstinValidating(true);
      try {
        const res = await axios.post(`${API}/validate-gstin`, { gstin }, { withCredentials: true });
        if (res.data.valid) {
          setGstinValid(true);
          if (res.data.pan) update("pan_number", res.data.pan);
          if (res.data.state) update("state", res.data.state);
        } else {
          setGstinValid(false);
        }
      } catch {
        setGstinValid(false);
      } finally {
        setGstinValidating(false);
      }
    }
  };

  const handlePaymentTermsChange = (val) => {
    const opt = PAYMENT_TERMS_OPTIONS.find(o => o.value === val);
    update("payment_terms", val);
    if (opt && opt.days >= 0) update("payment_terms_days", opt.days);
  };

  const handleSave = async () => {
    if (!form.company_name?.trim()) { toast.error("Company name is required"); return; }

    // Detect Non-GST switch when GSTIN was previously set and now cleared
    const previouslyHadGst = !!(company?.gst_number || "").trim();
    const trimmedGst = (form.gst_number || "").trim();
    if (!trimmedGst && (previouslyHadGst || company?.gst_registered === undefined)) {
      const ok = window.confirm(
        "GST number not provided. Invoices will be generated as Non-GST invoices without GST tax calculations.\n\nContinue without GSTIN?"
      );
      if (!ok) return;
    }

    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.company_id;
      delete payload.user_id;
      delete payload.created_at;
      delete payload.updated_at;
      if (typeof payload.hsn_codes === "string") {
        payload.hsn_codes = payload.hsn_codes.split(",").map(s => s.trim()).filter(Boolean);
      }
      payload.gst_registered = !!trimmedGst;
      const res = await axios.put(`${API}/companies/${defaultCompanyId}`, payload, { withCredentials: true });
      setCompany(res.data);
      setForm(res.data);
      toast.success("Company settings saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const [localLogoPreview, setLocalLogoPreview] = useState(null);
  const [localSigPreview, setLocalSigPreview] = useState(null);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please upload an image file"); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo must be under 2MB"); return; }
    // Instant local preview while uploading
    const localUrl = URL.createObjectURL(file);
    setLocalLogoPreview(localUrl);
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/companies/${defaultCompanyId}/upload-logo`, fd, { withCredentials: true });
      // Cache-bust so the <img> reloads even if URL path is reused
      const busted = `${res.data.logo_url}?t=${Date.now()}`;
      update("logo_url", busted);
      setCompany(prev => ({ ...prev, logo_url: busted }));
      toast.success("Logo uploaded");
    } catch {
      toast.error("Logo upload failed");
      setLocalLogoPreview(null);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSigUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please upload an image file"); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Signature must be under 2MB"); return; }
    const localUrl = URL.createObjectURL(file);
    setLocalSigPreview(localUrl);
    setUploadingSig(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/companies/${defaultCompanyId}/upload-signature`, fd, { withCredentials: true });
      const busted = `${res.data.signature_url}?t=${Date.now()}`;
      update("signature_url", busted);
      setCompany(prev => ({ ...prev, signature_url: busted }));
      toast.success("Signature uploaded");
    } catch {
      toast.error("Signature upload failed");
      setLocalSigPreview(null);
    } finally {
      setUploadingSig(false);
    }
  };

  const removeLogo = () => { update("logo_url", null); setLocalLogoPreview(null); };
  const removeSignature = () => { update("signature_url", null); setLocalSigPreview(null); };

  // Resolve any image URL: absolute → as-is, relative → prefix with backend URL (fixes share-page cross-origin)
  const resolveAssetUrl = (u) => {
    if (!u) return null;
    if (/^(https?:|blob:|data:)/.test(u)) return u;
    if (u.startsWith("/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
    return u;
  };

  const handleSaveApiKey = async () => {
    if (!apiKey.trim()) { toast.error("Please enter an API key"); return; }
    if (!apiKey.trim().startsWith("sk-")) { toast.error("Invalid key format. OpenAI keys start with 'sk-'"); return; }
    setSavingKey(true);
    try {
      const res = await axios.post(`${API}/settings/api-key`, { api_key: apiKey.trim() }, { withCredentials: true });
      setHasKey(true);
      setMaskedKey(res.data.masked_key);
      setApiKey("");
      setShowKey(false);
      toast.success("API key saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save API key");
    } finally {
      setSavingKey(false);
    }
  };

  const handleDeleteApiKey = async () => {
    if (!window.confirm("Remove your OpenAI API key?")) return;
    setDeletingKey(true);
    try {
      await axios.delete(`${API}/settings/api-key`, { withCredentials: true });
      setHasKey(false);
      setMaskedKey(null);
      toast.success("API key removed");
    } catch {
      toast.error("Failed to remove API key");
    } finally {
      setDeletingKey(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#2563EB]" />
      </div>
    );
  }

  if (!defaultCompanyId && !isCA) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center" data-testid="settings-no-company">
        <div className="text-center max-w-md px-4">
          <Building2 className="w-12 h-12 text-[#9CA3AF] mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-[#111827] mb-2">No Company Set Up</h2>
          <p className="text-[#6B7280] mb-6">Create your company first to access settings.</p>
          <Link to="/company/setup" className="btn-primary text-sm">Set Up Company</Link>
        </div>
      </div>
    );
  }

  const hsnDisplay = Array.isArray(form.hsn_codes) ? form.hsn_codes.join(", ") : (form.hsn_codes || "");
  const activeTabs = isCA ? CA_TABS : TABS;

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="settings-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#111827]">{isCA ? "Professional Settings" : "Company Settings"}</h1>
          <p className="text-sm text-[#6B7280] mt-1">{isCA ? "Manage your professional profile and preferences." : "Manage your company profile, invoice defaults, and integrations."}</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar Tabs */}
          <div className="lg:w-56 flex-shrink-0">
            <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-x-visible pb-2 lg:pb-0" data-testid="settings-tabs">
              {activeTabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    data-testid={`settings-tab-${tab.id}`}
                    className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                      activeTab === tab.id
                        ? "bg-[#2563EB] text-white"
                        : "text-[#4B5563] hover:bg-[#F3F4F6]"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_4px_20px_rgb(0,0,0,0.03)] p-5 sm:p-7"
            >
              {/* Professional Profile Tab (CA users) */}
              {activeTab === "profile" && (
                <div className="space-y-5" data-testid="settings-profile-tab">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-semibold text-[#111827]">Professional Profile</h2>
                      <p className="text-sm text-[#6B7280] mt-0.5">This information appears on your public profile page.</p>
                    </div>
                    {user?.professional_id && (
                      <span className="text-xs font-mono font-semibold bg-violet-50 text-violet-700 px-3 py-1.5 rounded-lg border border-violet-100" data-testid="settings-pro-id">
                        {user.professional_id}
                      </span>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#374151] mb-1.5">Display Name</label>
                    <div className="relative">
                      <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                      <Input
                        value={proProfile.name}
                        onChange={(e) => setProProfile(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="CA Rajesh Kumar"
                        className="pl-10"
                        data-testid="settings-pro-name"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-1.5">Firm / Practice Name</label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                        <Input
                          value={proProfile.firm_name}
                          onChange={(e) => setProProfile(prev => ({ ...prev, firm_name: e.target.value }))}
                          placeholder="Kumar & Associates"
                          className="pl-10"
                          data-testid="settings-pro-firm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-1.5">Specialization</label>
                      <div className="relative">
                        <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                        <Input
                          value={proProfile.specialization}
                          onChange={(e) => setProProfile(prev => ({ ...prev, specialization: e.target.value }))}
                          placeholder="GST Compliance, Tax Advisory"
                          className="pl-10"
                          data-testid="settings-pro-spec"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#374151] mb-1.5">Location</label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                      <Input
                        value={proProfile.location}
                        onChange={(e) => setProProfile(prev => ({ ...prev, location: e.target.value }))}
                        placeholder="Mumbai, Maharashtra"
                        className="pl-10"
                        data-testid="settings-pro-location"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#374151] mb-1.5">About You</label>
                    <Textarea
                      value={proProfile.bio}
                      onChange={(e) => setProProfile(prev => ({ ...prev, bio: e.target.value }))}
                      placeholder="Brief description of your practice, experience, and services..."
                      rows={3}
                      className="text-sm"
                      data-testid="settings-pro-bio"
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-2.5">
                      <Globe className="w-4 h-4 text-violet-500" />
                      <div>
                        <p className="text-sm font-medium text-slate-700">Public Profile</p>
                        <p className="text-xs text-slate-500">Allow clients to find you via your Professional ID</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setProProfile(prev => ({ ...prev, is_public: !prev.is_public }))}
                      className={`relative w-11 h-6 rounded-full transition-colors ${proProfile.is_public ? "bg-violet-600" : "bg-slate-300"}`}
                      data-testid="settings-pro-public-toggle"
                    >
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${proProfile.is_public ? "translate-x-5" : ""}`} />
                    </button>
                  </div>

                  {user?.professional_id && (
                    <div className="bg-violet-50 border border-violet-100 rounded-xl p-4">
                      <p className="text-xs text-violet-700">
                        Your public profile is live at:{" "}
                        <a
                          href={`/pro/${user.professional_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-semibold underline underline-offset-2"
                          data-testid="settings-pro-profile-link"
                        >
                          {window.location.origin}/pro/{user.professional_id}
                        </a>
                      </p>
                    </div>
                  )}

                  <div className="flex items-center justify-end pt-5 border-t border-[#F3F4F6]">
                    <Button
                      onClick={handleSaveProfile}
                      disabled={savingProfile}
                      className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white px-6"
                      data-testid="save-profile-btn"
                    >
                      {savingProfile ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                      Save Profile
                    </Button>
                  </div>
                </div>
              )}

              {/* Company Profile Tab */}
              {activeTab === "company" && (
                <div className="space-y-5" data-testid="settings-company-tab">
                  <h2 className="text-lg font-semibold text-[#111827]">Company Profile</h2>

                  <div>
                    <label className="block text-sm font-medium text-[#374151] mb-1.5">
                      GSTIN {gstinValidating && <Loader2 className="inline w-3 h-3 animate-spin ml-1" />}
                      {gstinValid === true && <CheckCircle2 className="inline w-4 h-4 text-[#10B981] ml-1" />}
                      {gstinValid === false && <span className="text-[#EF4444] text-xs ml-1">(Invalid)</span>}
                    </label>
                    <div className="relative">
                      <Receipt className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                      <Input
                        value={form.gst_number || ""}
                        onChange={(e) => handleGstinChange(e.target.value.toUpperCase())}
                        placeholder="29AABCT1234A1Z5"
                        className="pl-10 uppercase font-mono"
                        maxLength={15}
                        data-testid="field-gst-number"
                      />
                    </div>
                    {!(form.gst_number || "").trim() && (
                      <div className="mt-2 inline-flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-800" data-testid="non-gst-mode-hint">
                        <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                        <span>No GSTIN — your invoices will be generated in <strong>Non-GST mode</strong>. GST tax columns &amp; calculations are hidden.</span>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-1.5">Company Name *</label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                        <Input value={form.company_name || ""} onChange={(e) => update("company_name", e.target.value)} placeholder="Your Company Pvt Ltd" className="pl-10" data-testid="field-company-name" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-1.5">PAN Number</label>
                      <Input value={form.pan_number || ""} onChange={(e) => update("pan_number", e.target.value)} placeholder="AABCT1234A" data-testid="field-pan-number" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#374151] mb-1.5">Address</label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-3 w-4 h-4 text-[#9CA3AF]" />
                      <Textarea value={form.address || ""} onChange={(e) => update("address", e.target.value)} placeholder="123 MG Road, Bangalore 560001" className="pl-10 min-h-[72px]" data-testid="field-address" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-1.5">State</label>
                      <Input value={form.state || ""} onChange={(e) => update("state", e.target.value)} placeholder="Karnataka" data-testid="field-state" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-1.5">Website</label>
                      <div className="relative">
                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                        <Input value={form.website || ""} onChange={(e) => update("website", e.target.value)} placeholder="https://yourcompany.com" className="pl-10" data-testid="field-website" />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-1.5">Email</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                        <Input type="email" value={form.email || ""} onChange={(e) => update("email", e.target.value)} placeholder="billing@company.com" className="pl-10" data-testid="field-email" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-1.5">Phone</label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                        <Input value={form.phone || ""} onChange={(e) => update("phone", e.target.value)} placeholder="+91 98765 43210" className="pl-10" data-testid="field-phone" />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Invoice & Tax Tab */}
              {activeTab === "invoice" && (
                <div className="space-y-5" data-testid="settings-invoice-tab">
                  <h2 className="text-lg font-semibold text-[#111827]">Invoice & Tax Defaults</h2>
                  <p className="text-sm text-[#6B7280]">These defaults auto-fill when you create new invoices.</p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-1.5">Invoice Number Prefix</label>
                      <Input value={form.invoice_prefix ?? ""} onChange={(e) => update("invoice_prefix", e.target.value)} placeholder="INV" data-testid="field-invoice-prefix" />
                      <p className="text-xs text-[#9CA3AF] mt-1" data-testid="prefix-preview">
                        Preview: <span className="font-mono text-slate-600">{((form.invoice_prefix && form.invoice_prefix.trim()) || "INV").toUpperCase()}/{(() => { const d = new Date(); const y = d.getMonth() + 1 < 4 ? d.getFullYear() - 1 : d.getFullYear(); return `${y}-${String((y + 1) % 100).padStart(2, "0")}`; })()}/0001</span>
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-1.5">Default GST Rate</label>
                      <Select value={String(form.default_gst_percent ?? 18)} onValueChange={v => update("default_gst_percent", parseFloat(v))}>
                        <SelectTrigger data-testid="field-gst-rate"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">0% (Exempt)</SelectItem>
                          <SelectItem value="5">5%</SelectItem>
                          <SelectItem value="12">12%</SelectItem>
                          <SelectItem value="18">18% (Most common)</SelectItem>
                          <SelectItem value="28">28%</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-1.5">Default Tax Type</label>
                      <Select value={form.tax_type || "CGST_SGST"} onValueChange={v => update("tax_type", v)}>
                        <SelectTrigger data-testid="field-tax-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="CGST_SGST">CGST + SGST (Intra-state)</SelectItem>
                          <SelectItem value="IGST">IGST (Inter-state)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-1.5">Payment Terms</label>
                      <Select value={form.payment_terms || "Net 30"} onValueChange={handlePaymentTermsChange}>
                        <SelectTrigger data-testid="field-payment-terms"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {PAYMENT_TERMS_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label || opt.value}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {form.payment_terms === "Custom" && (
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-1.5">Custom Payment Days</label>
                      <Input type="number" min={1} max={365} value={form.payment_terms_days || ""} onChange={(e) => update("payment_terms_days", parseInt(e.target.value) || 0)} placeholder="45" data-testid="field-custom-days" />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-[#374151] mb-1.5">Common HSN/SAC Codes</label>
                    <Input value={hsnDisplay} onChange={(e) => update("hsn_codes", e.target.value)} placeholder="998361, 998313" data-testid="field-hsn-codes" />
                    <p className="text-xs text-[#9CA3AF] mt-1">Comma-separated. These appear as suggestions in the invoice builder.</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#374151] mb-1.5">Default Invoice Notes / Terms & Conditions</label>
                    <Textarea value={form.invoice_notes || ""} onChange={(e) => update("invoice_notes", e.target.value)} placeholder="Thank you for your business! Payment is due within the specified terms." className="min-h-[80px]" data-testid="field-invoice-notes" />
                    <p className="text-xs text-[#9CA3AF] mt-1">This text appears at the bottom of every generated invoice.</p>
                  </div>

                  <div className="p-3.5 bg-[#EFF6FF] rounded-xl border border-[#BFDBFE]">
                    <p className="text-sm text-[#1E40AF] font-medium">Smart GST Detection</p>
                    <p className="text-xs text-[#3B82F6] mt-0.5">GST type (CGST+SGST vs IGST) is auto-detected based on your state and customer state when creating invoices.</p>
                  </div>
                </div>
              )}

              {/* Bank & UPI Tab */}
              {activeTab === "bank" && (
                <div className="space-y-5" data-testid="settings-bank-tab">
                  <h2 className="text-lg font-semibold text-[#111827]">Bank & UPI Details</h2>
                  <p className="text-sm text-[#6B7280]">These details appear on your invoices for payment collection.</p>

                  <div>
                    <label className="block text-sm font-medium text-[#374151] mb-1.5">Account Holder Name</label>
                    <div className="relative">
                      <Landmark className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                      <Input value={form.bank_account_name || ""} onChange={(e) => update("bank_account_name", e.target.value)} placeholder="Your Company Pvt Ltd" className="pl-10" data-testid="field-bank-name" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#374151] mb-1.5">Account Number</label>
                    <Input value={form.bank_account_number || ""} onChange={(e) => update("bank_account_number", e.target.value)} placeholder="50100123456789" data-testid="field-bank-account" />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-1.5">IFSC Code</label>
                      <Input value={form.bank_ifsc || ""} onChange={(e) => update("bank_ifsc", e.target.value)} placeholder="HDFC0001234" data-testid="field-bank-ifsc" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#374151] mb-1.5">Bank Name</label>
                      <Input value={form.bank_name || ""} onChange={(e) => update("bank_name", e.target.value)} placeholder="HDFC Bank" data-testid="field-bank-branch" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#374151] mb-1.5">UPI ID</label>
                    <div className="relative">
                      <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                      <Input value={form.upi_id || ""} onChange={(e) => update("upi_id", e.target.value)} placeholder="company@upi" className="pl-10" data-testid="field-upi-id" />
                    </div>
                    <p className="text-xs text-[#9CA3AF] mt-1">A QR code for this UPI ID will appear on generated invoices.</p>
                  </div>
                </div>
              )}

              {/* Logo & Signature Tab */}
              {activeTab === "branding" && (
                <div className="space-y-6" data-testid="settings-branding-tab">
                  <h2 className="text-lg font-semibold text-[#111827]">Logo & Signature</h2>
                  <p className="text-sm text-[#6B7280]">Upload your company logo and authorized signatory image. These appear on generated invoices.</p>

                  {/* Logo */}
                  <div>
                    <label className="block text-sm font-medium text-[#374151] mb-3">Company Logo</label>
                    <div className="flex items-start gap-4">
                      <div className="w-24 h-24 rounded-xl border-2 border-dashed border-[#D1D5DB] bg-[#F9FAFB] flex items-center justify-center overflow-hidden flex-shrink-0">
                        {(localLogoPreview || form.logo_url) ? (
                          <img src={localLogoPreview || resolveAssetUrl(form.logo_url)} alt="Logo" className="w-full h-full object-contain" data-testid="logo-preview" />
                        ) : (
                          <Image className="w-8 h-8 text-[#D1D5DB]" />
                        )}
                      </div>
                      <div className="space-y-2">
                        <input type="file" ref={logoRef} accept="image/*" className="hidden" onChange={handleLogoUpload} />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => logoRef.current?.click()}
                          disabled={uploadingLogo}
                          data-testid="upload-logo-btn"
                        >
                          {uploadingLogo ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Upload className="w-4 h-4 mr-1.5" />}
                          {form.logo_url ? "Change Logo" : "Upload Logo"}
                        </Button>
                        {form.logo_url && (
                          <Button variant="ghost" size="sm" onClick={removeLogo} className="text-[#EF4444] hover:text-[#DC2626]" data-testid="remove-logo-btn">
                            <X className="w-4 h-4 mr-1" /> Remove
                          </Button>
                        )}
                        <p className="text-xs text-[#9CA3AF]">PNG, JPG up to 2MB. Recommended: 200x200px</p>
                      </div>
                    </div>
                  </div>

                  {/* Signature */}
                  <div>
                    <label className="block text-sm font-medium text-[#374151] mb-3">Authorized Signature</label>
                    <div className="flex items-start gap-4">
                      <div className="w-40 h-20 rounded-xl border-2 border-dashed border-[#D1D5DB] bg-[#F9FAFB] flex items-center justify-center overflow-hidden flex-shrink-0">
                        {(localSigPreview || form.signature_url) ? (
                          <img src={localSigPreview || resolveAssetUrl(form.signature_url)} alt="Signature" className="w-full h-full object-contain" data-testid="signature-preview" />
                        ) : (
                          <PenTool className="w-6 h-6 text-[#D1D5DB]" />
                        )}
                      </div>
                      <div className="space-y-2">
                        <input type="file" ref={sigRef} accept="image/*" className="hidden" onChange={handleSigUpload} />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => sigRef.current?.click()}
                          disabled={uploadingSig}
                          data-testid="upload-signature-btn"
                        >
                          {uploadingSig ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Upload className="w-4 h-4 mr-1.5" />}
                          {form.signature_url ? "Change Signature" : "Upload Signature"}
                        </Button>
                        {form.signature_url && (
                          <Button variant="ghost" size="sm" onClick={removeSignature} className="text-[#EF4444] hover:text-[#DC2626]" data-testid="remove-signature-btn">
                            <X className="w-4 h-4 mr-1" /> Remove
                          </Button>
                        )}
                        <p className="text-xs text-[#9CA3AF]">PNG with transparent background recommended. Max 2MB.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Custom Additional Fields Tab */}
              {activeTab === "custom" && (
                <div className="space-y-5" data-testid="settings-custom-tab">
                  <h2 className="text-lg font-semibold text-[#111827]">Additional Business Information</h2>
                  <p className="text-sm text-[#6B7280]">Add custom fields that appear in your invoice header under company details. Perfect for registration numbers, license IDs, etc.</p>

                  <div className="space-y-3">
                    {(form.custom_fields || []).map((field, i) => (
                      <div key={i} className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-3" data-testid={`custom-field-${i}`}>
                        <div className="flex-1">
                          <Input
                            value={field.field_name || ""}
                            onChange={e => {
                              const updated = [...(form.custom_fields || [])];
                              updated[i] = { ...updated[i], field_name: e.target.value };
                              update("custom_fields", updated);
                            }}
                            placeholder="Field Name (e.g. Udyam Number)"
                            className="text-sm mb-2"
                            data-testid={`custom-field-name-${i}`}
                          />
                          <Input
                            value={field.field_value || ""}
                            onChange={e => {
                              const updated = [...(form.custom_fields || [])];
                              updated[i] = { ...updated[i], field_value: e.target.value };
                              update("custom_fields", updated);
                            }}
                            placeholder="Value (e.g. UDYAM-XX-00-0012345)"
                            className="text-sm"
                            data-testid={`custom-field-value-${i}`}
                          />
                        </div>
                        <button
                          onClick={() => {
                            const updated = (form.custom_fields || []).filter((_, idx) => idx !== i);
                            update("custom_fields", updated);
                          }}
                          className="p-2 text-slate-400 hover:text-red-500 transition-colors flex-shrink-0"
                          data-testid={`remove-custom-field-${i}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}

                    <Button
                      variant="outline"
                      onClick={() => {
                        const updated = [...(form.custom_fields || []), { field_name: "", field_value: "" }];
                        update("custom_fields", updated);
                      }}
                      className="w-full border-dashed"
                      data-testid="add-custom-field-btn"
                    >
                      <Plus className="w-4 h-4 mr-1.5" /> Add Field
                    </Button>
                  </div>

                  <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mt-4">
                    <p className="text-xs font-semibold text-blue-800 mb-2">Examples of common fields:</p>
                    <div className="text-xs text-blue-600 space-y-1">
                      <p>Udyam Registration Number — UDYAM-XX-00-0012345</p>
                      <p>FSSAI License Number — 12345678901234</p>
                      <p>Drug License Number — DL/2026/XXXX</p>
                      <p>CIN / Registration No. — U72200XX2024PTC123456</p>
                      <p>Medical Registration No. — MCI/2020/XXXXX</p>
                    </div>
                  </div>
                </div>
              )}

              {/* CA Management Tab */}
              {activeTab === "ca" && (
                <FeatureGate feature="ca_collaboration">
                <div className="space-y-5" data-testid="settings-ca-tab">
                  <h2 className="text-lg font-semibold text-[#111827]">CA / Professional Management</h2>
                  <p className="text-sm text-[#6B7280]">Invite a Chartered Accountant to view your invoices and manage GST compliance.</p>

                  {/* Invite Form */}
                  <div className="bg-white border border-slate-200 rounded-xl p-4">
                    <h3 className="text-sm font-semibold text-slate-900 mb-3">Invite Your CA</h3>
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input value={caInviteEmail} onChange={e => setCaInviteEmail(e.target.value)} placeholder="ca@firm.com" className="pl-9 text-sm" data-testid="ca-invite-email" />
                      </div>
                      <Button onClick={handleInviteCA} disabled={invitingCA} className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-sm" data-testid="ca-invite-send-btn">
                        {invitingCA ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />} Invite
                      </Button>
                    </div>
                    <p className="text-xs text-slate-400 mt-2">They'll receive a notification to accept your invitation. Your CA/Professional can only <b>view</b> data — they cannot create or edit invoices.</p>
                  </div>

                  {/* Existing Mappings */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900 mb-3">Connected CAs</h3>
                    {caMappings.length > 0 ? (
                      <div className="space-y-2">
                        {caMappings.map(m => (
                          <div key={m.mapping_id} className="flex items-center justify-between bg-white border border-slate-200 rounded-xl p-3" data-testid={`ca-mapping-${m.mapping_id}`}>
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center text-violet-600 font-bold text-sm">
                                {(m.ca_name || m.ca_email || "?")[0].toUpperCase()}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-slate-900">{m.ca_name || m.ca_email}</p>
                                <p className="text-xs text-slate-500">{m.ca_email}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                m.status === "active" ? "bg-emerald-50 text-emerald-700" :
                                m.status === "invited" ? "bg-amber-50 text-amber-700" :
                                "bg-red-50 text-red-700"
                              }`}>{m.status}</span>
                              {m.status !== "revoked" && (
                                <Button size="sm" variant="ghost" onClick={() => handleRevokeCA(m.mapping_id)} className="text-xs text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`revoke-ca-${m.mapping_id}`}>
                                  <XCircle className="w-3.5 h-3.5 mr-1" /> Revoke
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-slate-50 rounded-xl p-6 text-center">
                        <Users className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                        <p className="text-sm text-slate-500">No CA/Professional connected yet. Invite one above.</p>
                      </div>
                    )}
                  </div>
                </div>
                </FeatureGate>
              )}

              {/* Security Tab — Set/Change Password (Account Linking) */}
              {activeTab === "security" && (
                <div className="space-y-5" data-testid="settings-security-tab">
                  <div>
                    <h2 className="text-lg font-semibold text-[#111827]">Security</h2>
                    <p className="text-sm text-[#6B7280] mt-0.5">Manage your password and login methods.</p>
                  </div>

                  {/* Login methods summary */}
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Active login methods</p>
                    <div className="flex flex-wrap gap-2">
                      {authProvider === "google" && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white border border-slate-200 rounded-full text-xs font-medium text-slate-700">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Google sign-in
                        </span>
                      )}
                      {hasPassword && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white border border-slate-200 rounded-full text-xs font-medium text-slate-700" data-testid="email-login-active">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Email + Password
                        </span>
                      )}
                      {!hasPassword && authProvider === "google" && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 border border-amber-200 rounded-full text-xs font-medium text-amber-700">
                          Email + Password &mdash; not set
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Password form */}
                  {hasPassword === null ? (
                    <div className="rounded-xl border border-slate-200 p-5">
                      <p className="text-sm text-slate-400 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Checking login methods…</p>
                    </div>
                  ) : (
                  <div className="rounded-xl border border-slate-200 p-5 space-y-4">
                    <h3 className="text-base font-semibold text-slate-900">
                      {hasPassword ? "Change password" : "Set a password"}
                    </h3>
                    {!hasPassword && (
                      <p className="text-xs text-slate-500 -mt-2">
                        Set a password to enable email/password login as an alternate option to Google.
                      </p>
                    )}

                    {hasPassword && (
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Current password</label>
                        <div className="relative">
                          <Input
                            type={pwdShow ? "text" : "password"}
                            value={pwdCurrent}
                            onChange={(e) => setPwdCurrent(e.target.value)}
                            placeholder="Enter current password"
                            data-testid="security-current-pwd"
                          />
                        </div>
                      </div>
                    )}
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">New password</label>
                      <div className="relative">
                        <Input
                          type={pwdShow ? "text" : "password"}
                          value={pwdNew}
                          onChange={(e) => setPwdNew(e.target.value)}
                          placeholder="Min 6 characters"
                          data-testid="security-new-pwd"
                        />
                        <button
                          type="button"
                          onClick={() => setPwdShow(!pwdShow)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {pwdShow ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Confirm new password</label>
                      <Input
                        type={pwdShow ? "text" : "password"}
                        value={pwdConfirm}
                        onChange={(e) => setPwdConfirm(e.target.value)}
                        placeholder="Re-enter new password"
                        data-testid="security-confirm-pwd"
                      />
                    </div>
                    <div className="flex items-center justify-end pt-2">
                      <button
                        onClick={handleSavePassword}
                        disabled={pwdSaving}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-50"
                        data-testid="security-save-pwd"
                      >
                        {pwdSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        {hasPassword ? "Change password" : "Set password"}
                      </button>
                    </div>
                  </div>
                  )}
                  {/* Replay Guided Tour */}
                  <div className="rounded-xl border border-slate-200 p-5 flex items-center justify-between gap-4" data-testid="replay-tour-card">
                    <div>
                      <h3 className="text-base font-semibold text-slate-900">Product tour</h3>
                      <p className="text-xs text-slate-500 mt-0.5">Replay the 5-step walkthrough of the dashboard, checklist, invoice creation & CA connect.</p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => {
                        navigate("/dashboard");
                        setTimeout(() => window.dispatchEvent(new CustomEvent("invoicesetu:replay-tour")), 400);
                      }}
                      className="text-sm flex-shrink-0"
                      data-testid="replay-tour-btn"
                    >
                      Replay tour
                    </Button>
                  </div>
                </div>
              )}
              {activeTab === "api" && (
                <div className="space-y-5" data-testid="settings-api-tab">
                  <h2 className="text-lg font-semibold text-[#111827]">OpenAI API Key</h2>
                  <p className="text-sm text-[#6B7280]">Use your own key for AI-powered invoice extraction and analysis.</p>

                  {hasKey ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-3 p-4 bg-[#F0FDF4] border border-[#BBF7D0] rounded-xl">
                        <CheckCircle2 className="w-5 h-5 text-[#10B981] flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#166534]">AI extraction active</p>
                          <p className="text-sm text-[#15803D] font-mono truncate">{maskedKey}</p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={handleDeleteApiKey} disabled={deletingKey} className="text-[#EF4444] hover:text-[#DC2626] hover:bg-[#FEE2E2]" data-testid="remove-key-btn">
                          {deletingKey ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="p-4 bg-[#FFF7ED] border border-[#FED7AA] rounded-xl">
                        <p className="text-sm text-[#9A3412]">Without an API key, invoices are analyzed using basic pattern matching. Add your OpenAI key for AI-powered extraction.</p>
                      </div>
                      <div className="flex gap-3">
                        <div className="relative flex-1">
                          <Input type={showKey ? "text" : "password"} placeholder="sk-..." value={apiKey} onChange={(e) => setApiKey(e.target.value)} className="pr-10 font-mono text-sm" data-testid="api-key-input" />
                          <button type="button" onClick={() => setShowKey(!showKey)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#6B7280]">
                            {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        <Button onClick={handleSaveApiKey} disabled={savingKey || !apiKey.trim()} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="save-key-btn">
                          {savingKey ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Key"}
                        </Button>
                      </div>
                      <p className="text-xs text-[#9CA3AF]">
                        Get your key from{" "}
                        <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-[#2563EB] hover:underline inline-flex items-center gap-0.5">
                          platform.openai.com <ExternalLink className="w-3 h-3" />
                        </a>
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Save Bar */}
              {activeTab !== "api" && activeTab !== "profile" && (
                <div className="flex items-center justify-end mt-6 pt-5 border-t border-[#F3F4F6]">
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-6"
                    data-testid="save-settings-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Changes
                  </Button>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
